import React, { useState } from 'react';
import { calculatePerformanceStats } from '../utils/performanceCalculations';

/**
 * PerformanceStats component that displays comprehensive trading performance metrics.
 * 
 * @param {Object} props
 * @param {Array} props.trades - Array of trade objects (used for client-side calculation if no backendStats)
 * @param {Object} props.backendStats - Pre-calculated stats from backend API (preferred over client-side calculation)
 * @param {boolean} props.loading - Whether stats are being loaded
 */
const PerformanceStats = ({ trades, backendStats, loading = false }) => {
  // Use backend stats if provided, otherwise calculate from trades
  const stats = backendStats ? {
    totalTrades: backendStats.total_trades || 0,
    winningTrades: backendStats.winning_trades || 0,
    losingTrades: backendStats.losing_trades || 0,
    winRate: backendStats.win_rate || 0,
    profitFactor: backendStats.profit_factor || 0,
    averageWin: backendStats.average_win || 0,
    averageLoss: backendStats.average_loss || 0,
    netProfit: backendStats.total_pnl || 0,
    bestTrade: backendStats.biggest_win || 0,
    worstTrade: backendStats.biggest_loss || 0,
    maxDrawdown: backendStats.max_drawdown || 0,
    maxDrawdownPct: backendStats.max_drawdown_pct || 0,
    averageR: backendStats.average_r || 0,
    averageDurationReadable: formatDuration(backendStats.average_duration_seconds || 0),
    sharpeRatio: backendStats.sharpe_ratio || 0,
    sortinoRatio: backendStats.sortino_ratio || 0,
    calmarRatio: backendStats.calmar_ratio || 0,
    expectancy: backendStats.expectancy || 0,
    payoffRatio: backendStats.payoff_ratio || 0,
    totalReturn: backendStats.total_pnl || 0,
    maxConsecutiveWins: backendStats.max_consecutive_wins || 0,
    maxConsecutiveLosses: backendStats.max_consecutive_losses || 0,
    longTrades: backendStats.long_trades || 0,
    shortTrades: backendStats.short_trades || 0,
    longWinRate: backendStats.long_win_rate || 0,
    shortWinRate: backendStats.short_win_rate || 0,
    averageTradePnl: backendStats.average_trade_pnl || 0,
  } : calculatePerformanceStats(trades);

  const [showMore, setShowMore] = useState(false);

  const StatCard = ({ label, value, className = '', valueClassName = '', subLabel = null }) => (
    <div className={`bg-backgroundBody p-3 rounded-lg shadow text-center ${className}`}>
      <p className="text-sm text-textSecondary font-medium">{label}</p>
      <p className={`text-xl font-bold ${valueClassName || 'text-textPrimary'}`}>{value}</p>
      {subLabel && <p className="text-xs text-textSecondary mt-1">{subLabel}</p>}
    </div>
  );

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return 'N/A';
    return Number(num).toFixed(decimals);
  };

  const getColorClass = (value, threshold = 0, invertLogic = false) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    if (invertLogic) {
      return num < threshold ? 'text-success' : num > threshold ? 'text-destructive' : '';
    }
    return num > threshold ? 'text-success' : num < threshold ? 'text-destructive' : '';
  };

  if (loading) {
    return (
      <div className="mb-6 p-4 bg-backgroundSurface border border-borderDefault rounded-lg">
        <h3 className="text-lg font-semibold mb-4 text-textPrimary">Performance Stats</h3>
        <div className="flex items-center justify-center py-8">
          <div className="animate-pulse text-textSecondary">Loading performance statistics...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 p-4 bg-backgroundSurface border border-borderDefault rounded-lg">
      <h3 className="text-lg font-semibold mb-4 text-textPrimary">Performance Stats</h3>
      
      {/* Primary Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard label="Total Trades" value={stats.totalTrades} />
        <StatCard 
          label="Win Rate" 
          value={`${formatNumber(stats.winRate)}%`} 
          className={getColorClass(stats.winRate, 50)}
        />
        <StatCard 
          label="Profit Factor" 
          value={formatNumber(stats.profitFactor)} 
          className={getColorClass(stats.profitFactor, 1)}
        />
        <StatCard label="Winning Trades" value={stats.winningTrades} className="text-success" />
        <StatCard label="Losing Trades" value={stats.losingTrades} className="text-destructive" />
        <StatCard 
          label="Avg. Win / Loss" 
          value={`$${formatNumber(stats.averageWin)} / $${formatNumber(stats.averageLoss)}`} 
        />
      </div>

      {/* Risk-Adjusted Returns Section */}
      <div className="mt-4 pt-4 border-t border-borderDefault">
        <h4 className="text-sm font-semibold mb-3 text-textSecondary uppercase tracking-wide">Risk-Adjusted Returns</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <StatCard 
            label="Sharpe Ratio" 
            value={formatNumber(stats.sharpeRatio)} 
            className={getColorClass(stats.sharpeRatio, 1)}
            subLabel="Risk-adjusted return"
          />
          <StatCard 
            label="Sortino Ratio" 
            value={formatNumber(stats.sortinoRatio)} 
            className={getColorClass(stats.sortinoRatio, 1)}
            subLabel="Downside risk only"
          />
          <StatCard 
            label="Calmar Ratio" 
            value={formatNumber(stats.calmarRatio)} 
            className={getColorClass(stats.calmarRatio, 1)}
            subLabel="Return / Max DD"
          />
          <StatCard 
            label="Payoff Ratio" 
            value={`${formatNumber(stats.payoffRatio)}:1`} 
            className={getColorClass(stats.payoffRatio, 1)}
            subLabel="Avg Win / Avg Loss"
          />
          <StatCard 
            label="Expectancy" 
            value={`$${formatNumber(stats.expectancy)}`} 
            className={getColorClass(stats.expectancy, 0)}
            valueClassName={getColorClass(stats.expectancy, 0) || 'text-textPrimary'}
            subLabel="Expected $ per trade"
          />
        </div>
      </div>

      {/* More stats accordion */}
      <div className="mt-3">
        <button
          type="button"
          onClick={() => setShowMore(v => !v)}
          className="text-sm text-primary hover:text-primaryHover flex items-center gap-1"
        >
          <span>{showMore ? '▼' : '▶'}</span>
          {showMore ? 'Hide detailed stats' : 'Show detailed stats'}
        </button>
        {showMore && (
          <div className="mt-4 space-y-4">
            {/* P&L Metrics */}
            <div>
              <h4 className="text-sm font-semibold mb-3 text-textSecondary uppercase tracking-wide">P&L Metrics</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <StatCard 
                  label="Net P/L" 
                  value={`$${formatNumber(stats.netProfit)}`} 
                  className={getColorClass(stats.netProfit, 0)}
                  valueClassName={getColorClass(stats.netProfit, 0) || 'text-textPrimary'}
                />
                <StatCard 
                  label="Best Trade" 
                  value={`$${formatNumber(stats.bestTrade)}`} 
                  className="text-success"
                  valueClassName="text-success"
                />
                <StatCard 
                  label="Worst Trade" 
                  value={`$${formatNumber(stats.worstTrade)}`} 
                  className="text-destructive"
                  valueClassName="text-destructive"
                />
                <StatCard 
                  label="Avg. Trade P/L" 
                  value={`$${formatNumber(stats.averageTradePnl)}`} 
                  className={getColorClass(stats.averageTradePnl, 0)}
                  valueClassName={getColorClass(stats.averageTradePnl, 0) || 'text-textPrimary'}
                />
                <StatCard 
                  label="Avg. R per Trade" 
                  value={`${formatNumber(stats.averageR)}R`} 
                />
              </div>
            </div>

            {/* Risk Metrics */}
            <div>
              <h4 className="text-sm font-semibold mb-3 text-textSecondary uppercase tracking-wide">Risk Metrics</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <StatCard 
                  label="Max Drawdown" 
                  value={`$${formatNumber(stats.maxDrawdown)}`} 
                  className="text-destructive"
                />
                <StatCard 
                  label="Max DD %" 
                  value={`${formatNumber(stats.maxDrawdownPct)}%`} 
                  className="text-destructive"
                />
                <StatCard 
                  label="Max Win Streak" 
                  value={stats.maxConsecutiveWins} 
                  className="text-success"
                />
                <StatCard 
                  label="Max Loss Streak" 
                  value={stats.maxConsecutiveLosses} 
                  className="text-destructive"
                />
              </div>
            </div>

            {/* Long/Short Breakdown */}
            <div>
              <h4 className="text-sm font-semibold mb-3 text-textSecondary uppercase tracking-wide">Long/Short Breakdown</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard 
                  label="Long Trades" 
                  value={stats.longTrades} 
                />
                <StatCard 
                  label="Long Win Rate" 
                  value={`${formatNumber(stats.longWinRate)}%`} 
                  className={getColorClass(stats.longWinRate, 50)}
                />
                <StatCard 
                  label="Short Trades" 
                  value={stats.shortTrades} 
                />
                <StatCard 
                  label="Short Win Rate" 
                  value={`${formatNumber(stats.shortWinRate)}%`} 
                  className={getColorClass(stats.shortWinRate, 50)}
                />
              </div>
            </div>

            {/* Time Metrics */}
            <div>
              <h4 className="text-sm font-semibold mb-3 text-textSecondary uppercase tracking-wide">Time Metrics</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <StatCard 
                  label="Avg. Trade Duration" 
                  value={stats.averageDurationReadable} 
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Helper function to format duration in seconds to readable string
 */
function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0m';
  
  const mins = Math.floor(seconds / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  const remMins = mins % 60;
  
  if (days > 0) return `${days}d ${remHours}h`;
  if (hours > 0) return `${hours}h ${remMins}m`;
  return `${remMins}m`;
}

export default PerformanceStats;
